import i18n from '@/i18n';
import { aiCopywritingAPI, taskAPI } from '@/utils/api';
import {
  appendCopywritingMaxCharsToPrompt,
  sanitizeCopywritingContent,
} from '@/pages/ipe/workbench/constants/aiCopywritingConfig';
import {
  isCustomGroupCopywritingResult,
  normalizeStoredImageGroups,
} from '@/pages/ipe/workbench/utils/aiCopywritingDetailUtils';

function normalizeTitleText(raw) {
  const text = String(raw || '').trim();
  return text.replace(/^(?:商品标题|产品标题|标题)\s*[:：]\s*/i, '').trim();
}

function resolveGroupReferenceUrl(result, groupIndex) {
  const stored = normalizeStoredImageGroups(result);
  if (stored.length > groupIndex && stored[groupIndex]?.length) {
    const img = stored[groupIndex][0];
    return String(img.apiUrl || img.previewUrl || '').trim();
  }
  const urls = Array.isArray(result.referenceUrls) ? result.referenceUrls : [];
  const previews = Array.isArray(result.referencePreviews) ? result.referencePreviews : [];
  return String(
    urls[groupIndex] || previews[groupIndex] || urls[0] || previews[0] || '',
  ).trim();
}

function resolveGroupCount(result) {
  const stored = normalizeStoredImageGroups(result);
  if (stored.length > 0) return stored.length;
  const urls = Array.isArray(result.referenceUrls) ? result.referenceUrls : [];
  const previews = Array.isArray(result.referencePreviews) ? result.referencePreviews : [];
  return Math.max(urls.length, previews.length, 1);
}

function replaceContentBlockAtIndex(content, groupIndex, refCount, newBlock) {
  const sanitized = sanitizeCopywritingContent(String(content || '').trim());
  const blocks = sanitized.split(/\n\n+/).map((part) => part.trim()).filter(Boolean);
  if (refCount <= 1) return newBlock;
  if (blocks.length === refCount) {
    const next = [...blocks];
    next[groupIndex] = newBlock;
    return next.join('\n\n');
  }
  if (blocks.length === 1 && groupIndex === 0) return newBlock;
  const next = [...blocks];
  while (next.length < refCount) next.push('');
  next[groupIndex] = newBlock;
  return next.filter((part, idx) => part || idx < refCount).join('\n\n');
}

/**
 * AI 文案详情：在当前任务内对指定组重新生成标题，替换原展示内容。
 */
export async function remakeAiCopywritingGroupInPlace({
  taskId,
  groupIndex,
  prompt = '',
  language,
  maxChars,
  titlePresetId = '',
  titlePresetName = '',
  titlePresetContent = '',
}) {
  const id = String(taskId || '').trim();
  const gi = Number(groupIndex);
  if (!id || !Number.isFinite(gi) || gi < 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.taskOrGroupInvalid'));
  }

  const { task: full } = await taskAPI.getById(id);
  if (!full) throw new Error(i18n.t('workbench:ipeUtilError.taskNotFound'));

  const prevResult = full.result && typeof full.result === 'object' ? { ...full.result } : {};
  const refUrl = resolveGroupReferenceUrl(prevResult, gi);
  if (!refUrl) {
    throw new Error(i18n.t('workbench:ipeUtilError.goodsGroupNotFound'));
  }

  const apiPrompt = String(prompt || '').trim();
  if (!apiPrompt) {
    throw new Error(i18n.t('workbench:ipeUtilError.selectCopyTemplateOrStyle'));
  }

  const requestId = `copy-remake-${id}-g${gi}-${Date.now()}`;
  const gen = await aiCopywritingAPI.submitAndWait({
    requestId,
    prompt: appendCopywritingMaxCharsToPrompt(apiPrompt, maxChars),
    referenceUrls: [refUrl],
    outputCount: 1,
    language: language || prevResult.language,
    maxChars: maxChars ?? prevResult.maxChars,
  });

  const newTitle = normalizeTitleText(gen?.content || gen?.text);
  if (!newTitle) {
    throw new Error(i18n.t('workbench:ipeUtilError.noNewCopyReturned'));
  }

  const refCount = resolveGroupCount(prevResult);
  const prevContent = String(prevResult.content || prevResult.text || '').trim();
  const nextContent = replaceContentBlockAtIndex(prevContent, gi, refCount, newTitle);

  const nextResult = {
    ...prevResult,
    prompt: apiPrompt,
    titlePresetId: titlePresetId || prevResult.titlePresetId,
    titlePresetName: titlePresetName || prevResult.titlePresetName,
    titlePresetContent: titlePresetContent || prevResult.titlePresetContent,
    language: language || prevResult.language,
    languageLabel: gen?.languageLabel || prevResult.languageLabel,
    maxChars: maxChars ?? prevResult.maxChars,
    content: nextContent,
    text: nextContent,
    batchPhase: 'completed',
    failureHint: '',
    serverWorker: true,
    uploadMode: isCustomGroupCopywritingResult(prevResult) ? 'custom' : prevResult.uploadMode,
  };

  const taskTotal = Math.max(Number(full.total) || 0, refCount, 1);
  await taskAPI.update(id, {
    status: 'completed',
    successCount: taskTotal,
    failedCount: 0,
    total: taskTotal,
    result: nextResult,
  });

  return {
    taskId: id,
    groupIndex: gi,
    title: newTitle,
    result: nextResult,
  };
}
